// jsonArgs.ts — pure parsing for the MCP servers page's "JSON array of
// strings" fields (container runtime args, stdio args).
//
// The page edits these as free text, so the text the user is typing (the
// draft) is kept separately from the committed string[]. parseArgsDraft says
// whether a draft is a valid commit and, when it is not, why — the reason is
// shown inline under the field instead of the edit being silently dropped.

export type ArgsParse =
  | { ok: true; value: string[] }
  | { ok: false; error: string };

/** formatArgs renders a committed array as the field's initial draft text. */
export function formatArgs(value: readonly string[] | undefined): string {
  return JSON.stringify(value ?? []);
}

/**
 * parseArgsDraft validates a draft. An empty (whitespace-only) draft means
 * "no args" and commits as []. Anything else must be a JSON array whose every
 * element is a string, because the values are passed verbatim as argv.
 */
export function parseArgsDraft(text: string): ArgsParse {
  if (text.trim() === '') return { ok: true, value: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return { ok: false, error: `Not valid JSON: ${detail}` };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: 'Must be a JSON array, e.g. ["--network=host"].' };
  }
  const bad = parsed.findIndex(v => typeof v !== 'string');
  if (bad >= 0) {
    return { ok: false, error: `Element ${bad} is not a string; every arg must be quoted.` };
  }
  return { ok: true, value: parsed as string[] };
}

/** sameArgs compares two arg arrays element-wise. */
export function sameArgs(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
